/**
 * ======================================
 *  API CONSTANTS — Tranzy Frontend
 * ======================================
 *
 *  Centralized source for API URLs and paths.
 *  Keeps endpoints consistent across features
 *  and makes version upgrades (v1 → v2) trivial.
 */

import { env } from "@/lib/env";

export const API_VERSION = "v1";

export const API_URL = env.API_BASE_URL.replace(/\/+$/, "");

/**
 * Root API prefix — `/api/v1` by default.
 * (Combines with baseURL in axios instance)
 */
export const API_PREFIX = `/api/${API_VERSION}`;

/**
 * All backend endpoint paths — organized by module.
 * Only server-side endpoints go here (not frontend routes).
 */
export const PATHS = {
  auth: {
    login: `${API_PREFIX}/auth/login`,
    selectRole: `${API_PREFIX}/auth/select-role`,
    me: `${API_PREFIX}/auth/me`,
    refresh: `${API_PREFIX}/auth/refresh`,
    logout: `${API_PREFIX}/auth/logout`,
    requestPasswordReset: `${API_PREFIX}/auth/forgot-password`,
    resetPassword: `${API_PREFIX}/auth/reset-password`,
    changePassword: `${API_PREFIX}/users/change-password`,
  },

  currencies: {
    base: `${API_PREFIX}/currencies`,
    public: `${API_PREFIX}/currencies/public`,
  },  
  airlines: {
    base: `${API_PREFIX}/airlines`,
  },
  airports: {
    base: `${API_PREFIX}/airports`,
  },

  locations: {
    base: "/api/v1/locations",
  },


  modules: {
    base: `${API_PREFIX}/modules`,
  },

  emailTemplates: {
    base: `${API_PREFIX}/email-templates`,
  },

  users: {
    base: `${API_PREFIX}/users`,
  },

  roles: {
    base: `${API_PREFIX}/roles`,
  },

  myaccount: {
    base: `${API_PREFIX}/myaccount`,
  },

  products: {
    base: `${API_PREFIX}/products`,
    public: `${API_PREFIX}/products/public`,
  },

  promos: {
    base: `${API_PREFIX}/promos`,
  },

  paymentLinks: {
    base: `${API_PREFIX}/payment-links`,
  },

  tours: {
    base: `${API_PREFIX}/tours`,
    aiImport: `${API_PREFIX}/tours/ai-import`,
    aiImportStatus: (jobId: string) =>
      `${API_PREFIX}/tours/ai-import/${encodeURIComponent(jobId)}`,
  },
  
  suppliers: {
    base: `${API_PREFIX}/suppliers`,
  },
  affiliatePartners: {
    base: `${API_PREFIX}/affiliate-partners`,
  },
  customers: {
    base: `${API_PREFIX}/customers`,
  },  
  leads: {
     base: `${API_PREFIX}/leads`,
  },
  uploads: {
     base: `${API_PREFIX}/uploads`,
  },
  tourTypes: { 
    base: `${API_PREFIX}/tour-types` 
  },
  hotelCatalogueAdmin: {
    coverage: `${API_PREFIX}/hotels/catalogue/coverage`,
  },
  hotelBookingsAdmin: {
    voucher: (bookingId: number | string) =>
      `${API_PREFIX}/admin/hotel-bookings/${bookingId}/voucher`,
    cancellation: (bookingId: number | string) =>
      `${API_PREFIX}/admin/hotel-bookings/${bookingId}/cancellation`,
    cancel: (bookingId: number | string) =>
      `${API_PREFIX}/admin/hotel-bookings/${bookingId}/cancel`,
  },
  bookingsAdmin: {
    base: `${API_PREFIX}/bookings/admin`,

    list: `${API_PREFIX}/bookings/admin/list`,
    search: `${API_PREFIX}/bookings/admin/search`,
    tourTabCounts: `${API_PREFIX}/bookings/admin/tours/tab-counts`,
    flightTabCounts: `${API_PREFIX}/bookings/admin/flights/tab-counts`,
    hotelTabCounts: `${API_PREFIX}/bookings/admin/hotels/tab-counts`,

    byId: (id: number | string) =>
      `${API_PREFIX}/bookings/admin/${id}`,

    updateStatus: (id: number | string) =>
      `${API_PREFIX}/bookings/admin/${id}/status`,

    delete: (id: number | string) =>
      `${API_PREFIX}/bookings/admin/${id}`,

    bulkDelete: `${API_PREFIX}/bookings/admin/bulk-delete`,

    edit: (id: number | string) =>
      `${API_PREFIX}/bookings/admin/${id}/edit`,

    travelers: (id: number | string) =>
      `${API_PREFIX}/bookings/admin/${id}/travelers`,

    checkout: (id: number | string) =>
      `${API_PREFIX}/bookings/admin/${id}/checkout`,

    paymentLink: (id: number | string) =>
      `${API_PREFIX}/bookings/admin/${id}/payment-link`,

    paymentLinkGenerate: (id: number | string) =>
    `${API_PREFIX}/bookings/admin/${id}/payment-link/generate`,
    
    travelPlan: (id: number | string) =>
      `${API_PREFIX}/bookings/admin/${id}/travel-plan`,

    generatePaymentLink: (id: string | number) =>
      `${API_PREFIX}/bookings/admin/${id}/payment-link/generate`,

    voucherGenerate: (id: number | string) =>
    `${API_PREFIX}/bookings/admin/${id}/voucher/generate`,
  },

  /* =========================
   * Dashboard (Admin)
   * ========================= */

  dashboard: {
    base: `${API_PREFIX}/dashboard`,

    adminSummary: `${API_PREFIX}/dashboard/admin/summary`,
  },


} as const;

/**
 * Common app routes (frontend only, optional).
 * These are NOT sent to the backend — just for redirect helpers.
 */
export const APP_ROUTES = {
  login: "/login",
  dashboard: "/dashboard",
  notFound: "/404",
} as const;
